import React, { useEffect, useState } from "react";
import { Facets } from "../types";
import { availabilityStatusMap, markerMap } from "../utils/constants";
import { EVENTS, eventBus } from "../utils/eventBus";
import { fetchFacetsData } from "../api/catalogApi";
import { parseQueryParams } from "../utils/queryParams";

interface FilterTagsProps {
  groupId: string;
}

export const FilterTags: React.FC<FilterTagsProps> = ({ groupId }) => {
  // Load facets data
  const [facets, setFacets] = useState<Facets | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, Set<string>>
  >({});

  // Initialize selected filters from URL on mount
  useEffect(() => {
    const params = parseQueryParams();
    const initialFilters: Record<string, Set<string>> = {};

    params.cp.forEach((filter) => {
      const [facet, value] = filter.split(":");
      if (facet && value) {
        if (!initialFilters[facet]) {
          initialFilters[facet] = new Set<string>();
        }
        initialFilters[facet].add(value);
      }
    });

    setSelectedFilters(initialFilters);
  }, []);

  useEffect(() => {
    const handleFacetsLoaded = (facetsData: Facets) => {
      setFacets(facetsData);
    };

    // Listen for filter changes and update selected filters
    const handleFiltersChanged = (params: any) => {
      const newFilters: Record<string, Set<string>> = {};

      params.cp.forEach((filter: string) => {
        const [facet, value] = filter.split(":");
        if (facet && value) {
          if (!newFilters[facet]) {
            newFilters[facet] = new Set<string>();
          }
          newFilters[facet].add(value);
        }
      });

      setSelectedFilters(newFilters);
    };

    eventBus.on(EVENTS.FACETS_LOADED, handleFacetsLoaded);
    eventBus.on(EVENTS.FILTERS_CHANGED, handleFiltersChanged);

    // Trigger a fetch on mount to ensure we get the data (from cache or API)
    // This will emit the event which our listener will catch
    const params = parseQueryParams();
    const queryString = new URLSearchParams(
      params.cp.map((filter) => ["cp[]", filter])
    ).toString();
    fetchFacetsData(groupId, queryString);

    // Cleanup listener on unmount
    return () => {
      eventBus.off(EVENTS.FACETS_LOADED, handleFacetsLoaded);
      eventBus.off(EVENTS.FILTERS_CHANGED, handleFiltersChanged);
    };
  }, [groupId]);

  // Load facets on mount
  // If no filters are selected, don't render anything
  if (Object.keys(selectedFilters).length === 0) {
    return null;
  }

  return (
    <div className="page-view-category-page--badges">
      <h2 className="text-lg font-['Montserrat-SemiBold'] font-semibold border-none mb-2">
        ODABRANI FILTERI
      </h2>
      <ul className="page-view-category-page--badges-list">
        {Object.entries(selectedFilters).map(([facetKey, values]) => {
          // Skip empty sets
          if (values.size === 0) return null;
          // Get the facet label
          return Array.from(values).map((value) => {
            // For attribute facets, we need to find the display value
            let displayValue = value.includes("|")
              ? value.split("|")[2]
              : value;
            if (facetKey === "attributes" || facetKey === "attributes_extra") {
              const parts = displayValue.split("|");
              const attribute_name_key = parts[0] + "_" + parts[1];
              // Find the attribute facet that contains this value
              Object.entries(facets || {}).forEach(([facetKey, facet]) => {
                if (
                  facet.type.toLocaleLowerCase() === "attribute" &&
                  facetKey === attribute_name_key
                ) {
                  const matchingValue = facet.values.find(
                    (v) => v.filter.toLowerCase() === parts[2].toLowerCase()
                  );
                  if (matchingValue) {
                    displayValue = matchingValue.filter;
                  }
                }
              });
            } else if (facetKey === "raspolozivo") {
              displayValue = availabilityStatusMap[value] || value;
            } else if (facetKey === "novo_best_price") {
              displayValue = markerMap[value] || value;
            } else if (
              facets &&
              facets[facetKey]?.type?.toLowerCase() === "shop"
            ) {
              // For shop facets, use the facet label as the display value
              displayValue = facets[facetKey].label || value;
            }

            return (
              <li key={`${facetKey}-${value}`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    eventBus.emit(EVENTS.FILTER_REMOVED, {
                      facet: facetKey,
                      value: value,
                    });
                  }}
                  className="page-view-category-page--badges-list__item"
                  aria-label="Remove filter"
                >
                  {displayValue}
                </button>
              </li>
            );
          });
        })}
      </ul>
    </div>
  );
};
